package io.janni.wedding.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.blur
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.janni.wedding.SubheadlineTextStyle
import io.janni.wedding.toSitePalette
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Br
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

val HeroContainerStyle = CssStyle {
    base { Modifier.fillMaxWidth().gap(2.cssRem) }
}

val ContentContainerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .minHeight(90.percent)
            .padding(2.cssRem)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .alignItems(AlignItems.Center)
            .gap(2.cssRem)
    }
    Breakpoint.MD {
        Modifier
            .padding(leftRight = 5.cssRem, topBottom = 2.cssRem)
            .display(DisplayStyle.Grid)
            .gap(2.cssRem)
            .alignItems(AlignItems.End)
            .styleModifier {
                property("grid-template-columns", "1fr 1fr 1fr")
                property("grid-template-rows", "1fr auto")
            }
    }
}

val TitleContainerStyle = CssStyle {
    base {
        Modifier
            .textAlign(TextAlign.Center)
    }
    Breakpoint.MD {
        Modifier
            .styleModifier {
                property("grid-column", "1 / 3")
                property("grid-row", "1")
                property("align-self", "start")
                property("padding-top", "2rem")
                property("text-align", "center")
                property("display", "flex")
                property("justify-content", "center")
                property("align-items", "center")
            }
    }
}

val ContentColumnStyle = CssStyle {
    base {
        Modifier
    }
    Breakpoint.MD {
        Modifier
            .styleModifier {
                property("grid-column", "2 / 4")
                property("grid-row", "2")
                property("align-self", "end")
            }
    }
}

val TitleTextStyle = CssStyle {
    base {
        Modifier.fontSize(4.cssRem)
    }
    Breakpoint.SM {
        Modifier.fontSize(6.cssRem)
    }
}

@Composable
fun Content() {
    Box(
        modifier = ContentContainerStyle.toModifier()
    ) {
        // Title in the first third (left side on desktop)
        Box(
            modifier = TitleContainerStyle.toModifier()
        ) {
            Title()
        }

        // Content in the last two thirds (bottom right on desktop)
        Column(
            modifier = ContentColumnStyle.toModifier()
                .then(HeroContainerStyle.toModifier())
                .fillMaxWidth()
                .maxWidth(870.px)
                .backgroundColor(Color.rgba(255, 255, 255, 0.2f))
                .borderRadius(16.px)
                .padding(3.cssRem)
                .backdropFilter(blur(4.px)),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Subtitle()
            Body()
        }
    }
}

@Composable
private fun Title() {
    val titleText = "Lisa & Jan-Niclas"
    var visibleLetters by remember { mutableStateOf(0) }

    LaunchedEffect(Unit) {
        for (i in 0..titleText.length) {
            visibleLetters = i
            delay(80) // Delay between each letter appearing
        }
    }

    Span(
        TitleTextStyle.toModifier()
            .fontWeight(400)
            .fontFamily("Tangerine")
            .lineHeight(1.2f)
            .styleModifier {
                property("text-shadow", "2px 2px 4px rgba(0, 0, 0, 0.3), 0px 0px 8px rgba(0, 0, 0, 0.2)")
            }
            .toAttrs()
    ) {
        titleText.forEachIndexed { index, char ->
            Span(
                Modifier
                    .styleModifier {
                        property("opacity", if (index < visibleLetters) "1" else "0")
                        property("transform", if (index < visibleLetters) "translateY(0)" else "translateY(-20px)")
                        property("transition", "opacity 0.3s ease-out, transform 0.3s ease-out")
                        property("display", "inline-block")
                        if (char == ' ') {
                            property("width", "0.3em")
                        }
                    }
                    .toAttrs()
            ) {
                Text(if (char == ' ') "\u00A0" else char.toString())
            }
        }
    }
}

@Composable
private fun Subtitle() = Column (
     SubheadlineTextStyle.toModifier()
        .textAlign(TextAlign.Center)
        .fontSize(1.5.cssRem)
        .fontWeight(500)
        .margin(bottom = 1.5.cssRem)
        .styleModifier {
            property("text-shadow", "2px 2px 4px rgba(0, 0, 0, 0.3), 0px 0px 8px rgba(0, 0, 0, 0.2)")
        }
){
    Text(
        "Wir haben Ja gesagt, am 15 August 2025",
    )
}

@Composable
private fun Body() = P(
    attrs = Modifier
        .textAlign(TextAlign.Justify)
        .styleModifier {
            property("text-shadow", "2px 2px 4px rgba(0, 0, 0, 0.3), 0px 0px 8px rgba(0, 0, 0, 0.2)")
        }
        .toAttrs()
) {
    SpanText("""
        Um 13:12 Uhr haben wir uns gegenseitig das Ja-Wort gegeben.
        Auf unserem wundervollen Hof, haben uns sogar unsere Pferde zu gewiehert.
        Wir wollen uns hiermit ausdrücklich für den wunderschönen Tag mit Euch
        bedanken, sowie die zahlreichen Geschenke, welche wir von Euch allen
        erhalten haben.
    """.trimIndent()
    )
    Br()
    Br()
    SpanText("""
        Unten in dem Picflow findet Ihr die Fotos von unserem Tag, ihr saht alle sehr
        schön aus. Danke dass ihr Teil unserer Hochzeit wart!
    """.trimIndent()
    )
    Br()
    Br()
    Link(
        "https://guidoknoll.picflow.com/devries",
        "\uD83D\uDD17 Picflow für de Vries von Guido Knoll",
        Modifier.color(ColorMode.current.toSitePalette().brand.accent),
        variant = UncoloredLinkVariant
    )
}
